import { Convert, Units } from '@topconagriculture/unit-of-measure';
import { RegionalSettingNames } from '@topconagriculture/frontend-utils';
import { TENANTS } from '@topconagriculture/theme';

import { PriceUnit } from '../core/shared.types';
import { PresetSettings } from '../core/api/preset/preset.types';
import { GroupingMode, ReportSettings } from '../store/entities/reports/reports.types';
import {
  DatatoolReportType,
  InventoryReportType,
  LotReports,
  ReportType,
} from '../store/entities/reports/reports.constants';
import { RecipeType } from '../store/entities/recipes/recipes.types';
import { DataTablePanelColumn } from '../shared/DataTablePanel/DataTablePanel';
import config from './config';
import { ScaleWeightUnitType } from '../store/entities/settings/settings.types';
import { AppliedPer, ChargePeriod, ChargeType } from '../store/entities/costTypes/costTypes.types';

export const translationsConfig: {
  defaultNS: string[];
  app: string;
  'app.conv': string;
  auth: string;
  common: string;
  companyUser: string;
  contacts: string;
  country: string;
  dashboard: string;
  dataTools: string;
  dates: string;
  feedingList: string;
  feedingPeriods: string;
  feeds: string;
  graphqlClient: string;
  groupTypes: string;
  hms: string;
  inventory: string;
  languages: string;
  lots: string;
  manage: string;
  map: string;
  metrics: string;
  mixers: string;
  operators: string;
  products: string;
  profiling: string;
  refusals: string;
  reports: string;
  settings: string;
  sharing: string;
  subscription: string;
  table: string;
  tooltips: string;
  widget: string;
  zones: string;
} = {
  defaultNS: [
    'app',
    'app.conv',
    'auth',
    'common',
    'companyUser',
    'contacts',
    'country',
    'dashboard',
    'dataTools',
    'dates',
    'feedingList',
    'feedingPeriods',
    'feeds',
    'graphqlClient',
    'groupTypes',
    'hms',
    'inventory',
    'languages',
    'lots',
    'manage',
    'map',
    'metrics',
    'mixers',
    'operators',
    'products',
    'profiling',
    'refusals',
    'reports',
    'settings',
    'sharing',
    'subscription',
    'table',
    'tooltips',
    'widget',
    'zones',
  ],
  app: 'app',
  'app.conv': 'app.conv',
  auth: 'auth',
  common: 'common',
  companyUser: 'companyUser',
  contacts: 'contacts',
  country: 'country',
  dashboard: 'dashboard',
  dataTools: 'dataTools',
  dates: 'dates',
  feedingList: 'feedingList',
  feedingPeriods: 'feedingPeriods',
  feeds: 'feeds',
  graphqlClient: 'graphqlClient',
  groupTypes: 'groupTypes',
  hms: 'hms',
  inventory: 'inventory',
  languages: 'languages',
  lots: 'lots',
  manage: 'manage',
  map: 'map',
  metrics: 'metrics',
  mixers: 'mixers',
  operators: 'operators',
  products: 'products',
  profiling: 'profiling',
  refusals: 'refusals',
  reports: 'reports',
  settings: 'settings',
  sharing: 'sharing',
  subscription: 'subscription',
  table: 'table',
  tooltips: 'tooltips',
  widget: 'widget',
  zones: 'zones',
};

const tenantId = config.TENANT_ID;

export const tenantAgentMap = {
  [TENANTS.TOPCON]: 'TAP Feed',
  [TENANTS.TRIOLIET]: 'TFM',
};

export const tenantMobileMap = {
  [TENANTS.TOPCON]: 'TAP Feed',
  [TENANTS.TRIOLIET]: 'Trioliet TFM',
};

export const tenantAgent = tenantAgentMap[tenantId] || '<unknown tenant>';
export const tenantMobile = tenantMobileMap[tenantId] || '<unknown tenant>';

export const notificationsConfig: {
  defaultConf: {};
  defaultErrorConf: {};
} = {
  defaultConf: {
    autoDismiss: 2,
    position: 'bc',
  },
  defaultErrorConf: {
    autoDismiss: 0,
    position: 'bc',
  },
};

export const dateRangePickerConfig = {
  yearsToGenerateInDropdown: 10,
};

export const languageItems = [
  { key: 'da', value: 'DAU' },
  { key: 'de', value: 'DEU' },
  { key: 'en', value: 'ENG' },
  { key: 'es', value: 'ESP' },
  { key: 'fr', value: 'FRA' },
  { key: 'hu', value: 'HUN' },
  { key: 'it', value: 'ITA' },
  { key: 'ja', value: 'JAP' },
  { key: 'nl', value: 'NLD' },
  { key: 'pt', value: 'POT' },
  { key: 'ru', value: 'RUS' },
  { key: 'tr', value: 'TUR' },
  { ket: 'cs', value: 'CES' },
];

export const decimalSeperators = ['.', ','];
export const thousandSeperators = [
  { value: '.', label: '.' },
  { value: ',', label: ',' },
  { value: ' ', label: '\u23b5' },
];
export const dateFormats = [
  'dd-MM-yyyy',
  'dd/MM/yyyy',
  'dd.MM.yyyy',
  'MM-dd-yyyy',
  'MM/dd/yyyy',
  'MM.dd.yyyy',
  'yyyy-MM-dd',
  'yyyy/MM/dd',
  'yyyy.MM.dd',
];

export const timeFormats = ['HH:mm', 'HH:mm:ss'];

export const routesConfig = {
  feedGroupParamName: ':feedGroupId',
};

export const uiConfig = {
  feedingPeriodsColors: ['005c90', '469d01', 'f19f1c', '02c3f3', 'c8ebb3', 'ffc630', '014268', '266700', '0293d7'],
  emptySelectKey: -1,
  defaultUom: {
    weight: Units.KilogramPerCubicMeter,
    area: Units.Hectare,
    areaAtDb: Units.SquareMeter,
    system: Convert.UnitOfMeasureSystem.INTERNATIONAL,
  },
};

export enum MixingTypeCodes {
  none = 0,
  timer = 1,
  rotations = 2,
}

const constants = {
  common: {
    currency: 'EUR',
  },
  translationsConfig,
  notificationsConfig,
  uiConfig,
  languageItems,
  routesConfig,
};

export const precisions = {
  ingredients: 4,
};

export const nameLenRestrictions = {
  short: 6,
  medium: 10,
  long: 20,
  ultraLong: 40,
};

export const decimalDigits = {
  default: 2,
};

export const inputLimits = {
  actualLoadPercentage: 99,
};

export enum Metric {
  price = 'price',
  weight = 'weight',
  volume = 'volume',
  density = 'density',
  weightPerAnimal = 'weightPerAnimal',
  pricePerAnimal = 'pricePerAnimal',
  pricePer100Weight = 'pricePer100Weight',
  weight100 = 'weight100',
}

export const MetricsInternational: { [key in Metric]: string } = {
  price: 'metrics:euro',
  weight: 'metrics:kg',
  volume: 'metrics:m³',
  density: 'metrics:kg/m³',
  weightPerAnimal: 'metrics:kg/animal',
  pricePerAnimal: 'metrics:euro/animal',
  pricePer100Weight: 'metrics:euro/100kg',
  weight100: 'metrics:kg100',
};

export const MetricsUk: { [key in Metric]: string } = {
  price: 'metrics:pound',
  weight: 'metrics:kg',
  volume: 'metrics:m³',
  density: 'metrics:kg/m³',
  weightPerAnimal: 'metrics:kg/animal',
  pricePerAnimal: 'metrics:pound/animal',
  pricePer100Weight: 'metrics:pound/100kg',
  weight100: 'metrics:kg100',
};

export const MetricsUs: { [key in Metric]: string } = {
  price: 'metrics:dollar',
  weight: 'metrics:lbs',
  volume: 'metrics:ft³',
  density: 'metrics:lbs/ft³',
  weightPerAnimal: 'metrics:lbs/animal',
  pricePerAnimal: 'metrics:dollar/animal',
  pricePer100Weight: 'metrics:dollar/100lbs',
  weight100: 'metrics:lbs100',
};

export const metricMap = {
  international: MetricsInternational,
  uk: MetricsUk,
  us: MetricsUs,
};

export enum Symbols {
  percentage = '%',
  degree = '°',
  emptyValue = '-',
}

export const getWeekStartsOn: Record<string, number> = {
  [Convert.UnitOfMeasureSystem.INTERNATIONAL]: 1,
  [Convert.UnitOfMeasureSystem.UK]: 1,
  [Convert.UnitOfMeasureSystem.US]: 0,
};

export enum DateFormats {
  date = 'date',
  dateTime = 'dateTime',
  time = 'time',
}

export const regionalSettings = [
  { label: 'Metric', value: 'int' },
  { label: 'Imperial', value: 'uk' },
  { label: 'Standard (US)', value: 'us' },
];

export const regionalSettingsConfig = {
  regionFallback: RegionalSettingNames.INTERNATIONAL,
};

export enum PresetKind {
  DEFAULT = 'Default',
  CUSTOM = 'Custom',
}

export enum PresetDateRanges {
  TODAY = 'today',
  YESTERDAY = 'yesterday',
  LAST_7_DAYS = 'last_7_days',
  LAST_MONTH = 'last_month',
}

export enum DateRangeType {
  DAYS_BACK = 'daysBack',
  LAST_MONTH = 'lastMonth',
  YESTERDAY = 'yesterday',
}

export const defaultReportPreset: PresetSettings = {
  dataSettings: {},
  viewSettings: {
    pageOrientation: 0,
  },
};

export const reportPreset: {
  [key in ReportType | DatatoolReportType | InventoryReportType]: PresetSettings;
} = {
  [ReportType.DryMatterEfficiency]: defaultReportPreset,
  [ReportType.IngredientsPremixUsage]: defaultReportPreset,
  [ReportType.LoadReport]: defaultReportPreset,
  [ReportType.DMIntakePerHead]: {
    ...defaultReportPreset,
    viewSettings: {
      ...defaultReportPreset.viewSettings,
      showCWChange: false,
    },
  },
  [ReportType.RationDelivery]: defaultReportPreset,
  [ReportType.GroupDelivery]: defaultReportPreset,
  [ReportType.OperatorAccuracy]: defaultReportPreset,
  [ReportType.IngredientUsageByPen]: defaultReportPreset,
  [ReportType.CattleMovements]: defaultReportPreset,
  [ReportType.ClosedLots]: defaultReportPreset,
  [DatatoolReportType.PenSettingChanges]: defaultReportPreset,
  [DatatoolReportType.RationSettingChanges]: defaultReportPreset,
  [DatatoolReportType.IngredientInventoryChanges]: defaultReportPreset,
  [DatatoolReportType.IngredientSettingChanges]: defaultReportPreset,
  [DatatoolReportType.IngredientPriceSettingChanges]: defaultReportPreset,
  [DatatoolReportType.IngredientDryMatterSettingChanges]: defaultReportPreset,
  [InventoryReportType.Inventory]: defaultReportPreset,
  [InventoryReportType.InventoryProjectedRequirements]: defaultReportPreset,
  [InventoryReportType.StockTransaction]: {
    dataSettings: {},
    viewSettings: {
      pageOrientation: 2,
    },
  },
};

const defaultErrorTolerance = {
  errorTolerance: 5,
  showInsideTolerance: true,
  showOutsideTolerance: true,
};

const defaultMaxTrend = {
  maxTrend: 5,
};

const defaultUsage = {
  usePlannedUsage: 'true',
};

export const reportSettings: {
  [key in ReportType | DatatoolReportType | InventoryReportType]: ReportSettings;
} = {
  [ReportType.DryMatterEfficiency]: {
    ...defaultMaxTrend,
    viewSettings: {
      showDmeCM: false,
      showFatProtein: false,
    },
  },
  [ReportType.IngredientsPremixUsage]: {
    ...defaultErrorTolerance,
    priceUnitCode: -1,
  },
  [ReportType.LoadReport]: {
    ...defaultErrorTolerance,
    showLoads: true,
    showDeliveries: true,
    viewSettings: {
      showDryMatterPerc: false,
    },
    dataSettings: {
      showBatchNo: false,
    },
  },
  [ReportType.DMIntakePerHead]: defaultMaxTrend,
  [ReportType.RationDelivery]: {
    ...defaultErrorTolerance,
    ...defaultMaxTrend,
  },
  [ReportType.GroupDelivery]: {
    ...defaultErrorTolerance,
    ...defaultMaxTrend,
  },
  [ReportType.OperatorAccuracy]: defaultErrorTolerance,
  [ReportType.IngredientUsageByPen]: {
    priceUnitCode: -1,
    viewSettings: {
      groupingMode: GroupingMode.GROUP,
    },
  },
  [ReportType.CattleMovements]: {},
  [ReportType.ClosedLots]: { filterMode: 2 },
  [DatatoolReportType.PenSettingChanges]: {
    showDisplayNames: false,
  },
  [DatatoolReportType.RationSettingChanges]: {
    showDisplayNames: false,
  },
  [DatatoolReportType.IngredientSettingChanges]: {
    showDisplayNames: false,
  },
  [DatatoolReportType.IngredientPriceSettingChanges]: {
    showDisplayNames: false,
  },
  [DatatoolReportType.IngredientDryMatterSettingChanges]: {
    showDisplayNames: false,
  },
  [DatatoolReportType.IngredientInventoryChanges]: {
    showDisplayNames: false,
  },
  [InventoryReportType.Inventory]: {},
  [InventoryReportType.InventoryProjectedRequirements]: { ...defaultUsage },
  [InventoryReportType.StockTransaction]: {
    dataSettings: {
      daysBack: 14,
    },
    viewSettings: {
      ...defaultReportPreset.viewSettings,
      pageOrientation: 2,
      collapsed: 'true',
    },
  },
};

export const datatoolReportSettings: { [key in DatatoolReportType]: ReportSettings } = {
  [DatatoolReportType.PenSettingChanges]: {
    showDisplayNames: false,
  },
  [DatatoolReportType.RationSettingChanges]: {
    showDisplayNames: false,
  },
  [DatatoolReportType.IngredientSettingChanges]: {
    showDisplayNames: false,
  },
  [DatatoolReportType.IngredientPriceSettingChanges]: {
    showDisplayNames: false,
  },
  [DatatoolReportType.IngredientDryMatterSettingChanges]: {
    showDisplayNames: false,
  },
  [DatatoolReportType.IngredientInventoryChanges]: {
    showDisplayNames: false,
  },
};

export const API_CLIENTS = {
  graphqlClient: 'graphqlClient',
};

const priceUnitsUS = [
  {
    code: 0,
    label: 'metrics:lbs',
  },
  {
    code: 1,
    label: 'metrics:lbs100',
  },
  {
    code: 2,
    label: 'metrics:lbs1000',
  },
  {
    code: 3,
    label: 'metrics:ton',
  },
  {
    code: -1,
    label: 'products:use_ingredients_units',
  },
] as PriceUnit[];

const priceUnitsInt = [
  {
    code: 0,
    label: 'metrics:kg',
  },
  {
    code: 1,
    label: 'metrics:kg100',
  },
  {
    code: 2,
    label: 'metrics:tonne',
  },
  {
    code: -1,
    label: 'products:use_ingredients_units',
  },
] as PriceUnit[];

export const priceUnits = {
  [RegionalSettingNames.US]: priceUnitsUS,
  [RegionalSettingNames.UK]: priceUnitsInt,
  [RegionalSettingNames.INTERNATIONAL]: priceUnitsInt,
};

export const reportsDefaultFilenameKeys = {
  [ReportType.DMIntakePerHead]: 'reports:download_dm_intake_default_filename',
  [ReportType.GroupDelivery]: 'reports:download_group_delivery_default_filename',
  [ReportType.IngredientsPremixUsage]: 'reports:download_ingredient_premix_default_filename',
  [ReportType.LoadReport]: 'reports:download_load_default_filename',
  [ReportType.OperatorAccuracy]: 'reports:download_operator_accuracy_default_filename',
  [ReportType.RationDelivery]: 'reports:download_ration_delivery_default_filename',
  [ReportType.IngredientUsageByPen]: 'reports:download_ingredient_usage_group_filename',
  [ReportType.DryMatterEfficiency]: 'reports:download_dry_matter_efficiency_filename',
  [ReportType.CattleMovements]: 'reports:download_cattle_movements_filename',
  [ReportType.ClosedLots]: 'reports:download_closed_lots_filename',
  [InventoryReportType.Inventory]: 'reports:download_inventory_overview_default_filename',
  [InventoryReportType.InventoryProjectedRequirements]: 'reports:download_projected_requirements_filename',
  [LotReports.CostSummary]: 'lots:download_cost_summary_filename',
};

export const recipeTypeItems = {
  [RecipeType.Unknown]: {
    id: 0,
    descriptionKey: RecipeType.Unknown.toString(),
  },
  [RecipeType.Default]: {
    id: 1,
    descriptionKey: RecipeType.Default.toString(),
  },
  [RecipeType.AddToBaseRecipe]: {
    id: 2,
    descriptionKey: RecipeType.AddToBaseRecipe.toString(),
  },
  [RecipeType.RefusalSubstitute]: {
    id: 3,
    descriptionKey: RecipeType.RefusalSubstitute.toString(),
  },
  [RecipeType.IngredientResize]: {
    id: 4,
    descriptionKey: RecipeType.IngredientResize.toString(),
  },
};

export const timeZone = new Intl.DateTimeFormat().resolvedOptions().timeZone;

export const notInUseItemColor = 'rgb(229, 229, 229)';
export const medicineColor = 'rgb(219, 208, 251)';
export const refusalColor = 'rgb(230, 245, 226)';
export const premixColor = 'rgb(255, 255, 207)';
export const addToBaseRationColor = 'rgb(255, 178, 100)';
export const addToBaseRationIngredientColor = 'rgba(255, 178, 100, 0.53)';
export const addToBaseRationNestedGroupColor = 'rgb(247, 207, 168)';

export enum MovementTypeCode {
  Arrival = 1,
  Transfer = 2,
  Death = 3,
  Dispatch = 4,
}

export enum MovementType {
  Arrival = 'Arrival',
  In = 'In',
  Out = 'Out',
  Death = 'Death',
  Dispatch = 'Dispatch',
}

export enum IngredientTypeCode {
  NotSpecified = 0,
  Roughage = 1,
  ByProduct = 2,
  Concentrate = 3,
  FreshGrass = 4,
  Mineral = 5,
  Medication = 6,
  Refusal = 98,
  Premix = 99,
}

export enum ReportIngredientTypeCode {
  NotSpecified = '00000000-0000-0000-0000-000000000000',
  Roughage = '00000001-0000-0000-0000-000000000000',
  ByProduct = '00000002-0000-0000-0000-000000000000',
  Concentrate = '00000003-0000-0000-0000-000000000000',
  FreshGrass = '00000004-0000-0000-0000-000000000000',
  Mineral = '00000005-0000-0000-0000-000000000000',
  Medication = '00000006-0000-0000-0000-000000000000',
  Refusal = '00000098-0000-0000-0000-000000000000',
  Premix = '00000099-0000-0000-0000-000000000000',
}

export enum RationTypeCode {
  Unknown = 0,
  Standard = 1,
  AddToBaseRation = 2,
  RefusalSubstitution = 3,
  FirstIngredientResize = 4,
}

export enum MixerStatusCode {
  Active = 1,
  Verify = 2,
  InService = 3,
  NotInUse = 4,
}

export const timeZones = [
  'Etc/GMT+12',
  'Etc/GMT+11',
  'America/Adak',
  'Pacific/Honolulu',
  'Pacific/Marquesas',
  'America/Anchorage',
  'Etc/GMT+9',
  'America/Tijuana',
  'Etc/GMT+8',
  'America/Los_Angeles',
  'America/Phoenix',
  'America/Chihuahua',
  'America/Denver',
  'America/Whitehorse',
  'America/Guatemala',
  'America/Chicago',
  'Pacific/Easter',
  'America/Mexico_City',
  'America/Regina',
  'America/Bogota',
  'America/Cancun',
  'America/New_York',
  'America/Port-au-Prince',
  'America/Havana',
  'America/Indianapolis',
  'America/Grand_Turk',
  'America/Asuncion',
  'America/Halifax',
  'America/Caracas',
  'America/Cuiaba',
  'America/La_Paz',
  'America/Santiago',
  'America/St_Johns',
  'America/Araguaina',
  'America/Sao_Paulo',
  'America/Cayenne',
  'America/Buenos_Aires',
  'America/Godthab',
  'America/Montevideo',
  'America/Punta_Arenas',
  'America/Miquelon',
  'America/Bahia',
  'Etc/GMT+2',
  'Atlantic/Azores',
  'Atlantic/Cape_Verde',
  'Etc/GMT',
  'Europe/London',
  'Atlantic/Reykjavik',
  'Africa/Sao_Tome',
  'Africa/Casablanca',
  'Europe/Berlin',
  'Europe/Budapest',
  'Europe/Paris',
  'Europe/Warsaw',
  'Africa/Lagos',
  'Asia/Amman',
  'Europe/Bucharest',
  'Asia/Beirut',
  'Africa/Cairo',
  'Europe/Chisinau',
  'Asia/Damascus',
  'Asia/Hebron',
  'Africa/Johannesburg',
  'Europe/Kiev',
  'Asia/Jerusalem',
  'America/Whitehorse',
  'Europe/Kaliningrad',
  'Africa/Khartoum',
  'Africa/Tripoli',
  'Africa/Windhoek',
  'Asia/Baghdad',
  'Europe/Istanbul',
  'Asia/Riyadh',
  'Europe/Minsk',
  'Europe/Moscow',
  'Africa/Nairobi',
  'Europe/Volgograd',
  'Asia/Tehran',
  'Asia/Dubai',
  'Europe/Astrakhan',
  'Asia/Baku',
  'Europe/Samara',
  'Indian/Mauritius',
  'Europe/Saratov',
  'Asia/Tbilisi',
  'Asia/Yerevan',
  'Asia/Kabul',
  'Asia/Tashkent',
  'Asia/Yekaterinburg',
  'Asia/Karachi',
  'Asia/Qyzylorda',
  'Asia/Calcutta',
  'Asia/Colombo',
  'Asia/Katmandu',
  'Asia/Almaty',
  'Asia/Dhaka',
  'Asia/Omsk',
  'Asia/Rangoon',
  'Asia/Bangkok',
  'Asia/Barnaul',
  'Asia/Hovd',
  'Asia/Krasnoyarsk',
  'Asia/Novosibirsk',
  'Asia/Tomsk',
  'Asia/Shanghai',
  'Asia/Irkutsk',
  'Asia/Singapore',
  'Australia/Perth',
  'Asia/Taipei',
  'Asia/Ulaanbaatar',
  'Australia/Eucla',
  'Asia/Chita',
  'Asia/Tokyo',
  'Asia/Pyongyang',
  'Asia/Seoul',
  'Asia/Yakutsk',
  'Australia/Adelaide',
  'Australia/Darwin',
  'Australia/Brisbane',
  'Australia/Sydney',
  'Pacific/Port_Moresby',
  'Australia/Hobart',
  'Asia/Vladivostok',
  'Australia/Lord_Howe',
  'Pacific/Bougainville',
  'Asia/Srednekolymsk',
  'Asia/Magadan',
  'Pacific/Norfolk',
  'Asia/Sakhalin',
  'Pacific/Guadalcanal',
  'Asia/Kamchatka',
  'Pacific/Auckland',
  'Etc/GMT-12',
  'Pacific/Fiji',
  'Pacific/Chatham',
  // 'Etc/GMT-13',
  'Pacific/Tongatapu',
  'Pacific/Apia',
  'Pacific/Kiritimati',
  'Africa/Juba',
];

export enum FeedingListViewSettingsFields {
  ShowDisplayName = 'showDisplayName',
  ShowRation = 'showRation',
  ShowPenType = 'showPenType',
  ShowNoAnimals = 'showNoAnimals',
  ShowDailyRequirement = 'showDailyRequirement',
  ShowActualWeightPerHead = 'showActualWeightPerHead',
  ShowActualWeight = 'showActualWeight',
  ShowActualWeightPreviousDay = 'showActualWeightPreviousDay',
  ShowRefusalPreviousDay = 'showRefusalPreviousDay',
  ShowDryWeightPerHead = 'showDryWeightPerHead',
  ShowDryWeight = 'showDryWeight',
  ShowDMIAverage = 'showDMIAverage',
  ShowFeedings = 'showFeedings',
}

export const feedingListColumns: DataTablePanelColumn[] = [
  {
    label: 'feedingList:feeding_column_display_name',
    name: 'displayName',
    alignLeft: true,
    viewSettingsName: FeedingListViewSettingsFields.ShowDisplayName,
  },
  {
    label: 'feedingList:feeding_column_description',
    name: 'description',
    alignLeft: true,
  },
  {
    label: 'feedingList:feeding_column_recipe',
    name: 'rationNames',
    alignLeft: true,
    viewSettingsName: FeedingListViewSettingsFields.ShowRation,
  },
  {
    name: 'groupTypeDescr',
    alignLeft: true,
    label: 'feeds:group_type',
    viewSettingsName: FeedingListViewSettingsFields.ShowPenType,
  },
  {
    label: 'feedingList:feeding_column_animal_number',
    name: 'numberOfCows',
    footerColumn: true,
    alignLeft: false,
    viewSettingsName: FeedingListViewSettingsFields.ShowNoAnimals,
  },
  {
    label: 'feedingList:feeding_column_daily_requirement',
    name: 'globalFeedingFactor',
    alignLeft: false,
    viewSettingsName: FeedingListViewSettingsFields.ShowDailyRequirement,
  },
  {
    name: 'actualWeightHead',
    label: 'feedingList:actual_weight_head',
    viewSettingsName: FeedingListViewSettingsFields.ShowActualWeightPerHead,
  },
  {
    label: 'feedingList:feeding_column_actual_weight',
    name: 'actualWeightTotal',
    footerColumn: true,
    alignLeft: false,
    viewSettingsName: FeedingListViewSettingsFields.ShowActualWeight,
  },
  {
    name: 'previousActualWeight',
    label: 'feedingList:feeding_column_previous_day_actual_weight',
    viewSettingsName: FeedingListViewSettingsFields.ShowActualWeightPreviousDay,
  },
  {
    name: 'previousRefusal',
    label: 'feedingList:feeding_column_previous_day_refusal',
    viewSettingsName: FeedingListViewSettingsFields.ShowRefusalPreviousDay,
  },
  {
    name: 'dryWeightHead',
    label: 'feedingList:dry_weight_head',
    viewSettingsName: FeedingListViewSettingsFields.ShowDryWeightPerHead,
  },
  {
    name: 'dryWeightTotal',
    label: 'feeds:dry_weight',
    viewSettingsName: FeedingListViewSettingsFields.ShowDryWeight,
  },
  {
    name: 'averageDMI',
    label: 'feedingList:feeding_column_dmi_7_days_average',
    viewSettingsName: FeedingListViewSettingsFields.ShowDMIAverage,
  },
];

export enum InventoryReportViewSettingsFields {
  ShowStockPerc = 'showStockPerc',
  ShowAverageDailyUsage = 'showAverageDailyUsage',
  ShowExpectedOutOfStock = 'showExpectedOutOfStock',
  ShowDaysLeft = 'showDaysLeft',
  showDryMatterPerc = 'showDryMatterPerc',
  showMaxStock = 'showMaxStock',
  showStock = 'showStock',
  showDryStock = 'showDryStock',
  showPriceUnit = 'showPriceUnit',
  showPrice = 'showPrice',
  showTotalPrice = 'showTotalPrice',
  showOrderDate = 'showOrderDate',
}

export const inventoryReportColumns = [
  {
    name: 'ingredientName',
    nonRemovable: true,
  },
  {
    name: 'stockPerc',
    viewSettingsName: InventoryReportViewSettingsFields.ShowStockPerc,
    fixedWidth: 150,
  },
  {
    name: 'averageDailyUsage',
    viewSettingsName: InventoryReportViewSettingsFields.ShowAverageDailyUsage,
  },
  {
    name: 'expectedOutOfStock',
    viewSettingsName: InventoryReportViewSettingsFields.ShowExpectedOutOfStock,
  },
  {
    name: 'stockDaysLeft',
    viewSettingsName: InventoryReportViewSettingsFields.ShowDaysLeft,
  },
  {
    name: 'dryMatterPerc',
    viewSettingsName: InventoryReportViewSettingsFields.showDryMatterPerc,
  },
  {
    name: 'maxStock',
    footerColumn: true,
    viewSettingsName: InventoryReportViewSettingsFields.showMaxStock,
  },
  {
    name: 'stock',
    footerColumn: true,
    viewSettingsName: InventoryReportViewSettingsFields.showStock,
  },
  {
    name: 'dryStock',
    footerColumn: true,
    viewSettingsName: InventoryReportViewSettingsFields.showDryStock,
  },
  {
    name: 'price',
    viewSettingsName: InventoryReportViewSettingsFields.showPrice,
  },
  {
    name: 'priceUnit',
    footerColumn: true,
    viewSettingsName: InventoryReportViewSettingsFields.showPriceUnit,
  },
  {
    name: 'totalPrice',
    footerColumn: true,
    viewSettingsName: InventoryReportViewSettingsFields.showTotalPrice,
  },
  { name: 'orderDate', footerColumn: false, viewSettingsName: InventoryReportViewSettingsFields.showOrderDate },
  { name: 'action', label: '', footerColumn: false, nonRemovable: true },
  {
    name: 'goTo',
    label: '',
    footerColumn: true,
    nonRemovable: true,
  },
];

export const labelToTypeMap = { last_month: 'lastMonth', yesterday: 'yesterday' };

export const metricsMap: Record<ScaleWeightUnitType, string> = {
  [ScaleWeightUnitType.KG]: 'metrics:kg',
  [ScaleWeightUnitType.LBS]: 'metrics:lbs',
};
export const weightUnitTranslationMap = {
  [ScaleWeightUnitType.KG]: 'reports:fpcm',
  [ScaleWeightUnitType.LBS]: 'reports:ecm',
};

export const chargeTypeLabels: Record<ChargeType, string> = {
  [ChargeType.LOT]: 'lots:lot',
  [ChargeType.GROUP]: 'feeds:group',
};

export const chargePerLabels: Record<AppliedPer, string> = {
  [AppliedPer.ANIMAL]: 'reports:animal',
  [AppliedPer.LOT]: 'lots:lot',
  [AppliedPer.GROUP]: 'feeds:group',
};

export const chargePeriodLabels: Record<ChargePeriod, string> = {
  [ChargePeriod.DAY]: 'lots:day',
  [ChargePeriod.ON_SPECIFIC_TIMESTAMP]: 'lots:one_time_entry',
};

export const FLOATING_SCROLLBAR_CLASSNAME = 'spacious-container';

export default constants;
